import { useState, useMemo, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { debounce } from "lodash"
import { EditOutlined, PlusCircleFilled } from "@ant-design/icons"
import { Checkbox, Input, Flex, Popover, Button, Spin, Empty } from "antd"
import { DSIcon } from "ui/Icon/DSIcon"
import { VISIBLE_COUNT, SEARCH_DELAY } from "./constants"
import styles from "./BookingTable.module.css"

const EditableTagCell = ({ initialItems, allItems, groups, renderLabel, addLabel, chipClassName, onAdd, onRemove, onPickerOpen, onEditStart, onSearch, loading, removeLoading, emptyLabel, alwaysEditable }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [removingId, setRemovingId] = useState(null)
  const [items, setItems] = useState(initialItems)
  const [search, setSearch] = useState("")
  const [selectedIds, setSelectedIds] = useState([])

  // Keep the latest onSearch without recreating the debounced fn each render
  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  // Debounce only the (optional) server-side search callback — local filtering stays instant
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        onSearchRef.current?.(value)
      }, SEARCH_DELAY),
    []
  )

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  // In always-editable mode (booking detail) the cell renders chips-with-× up front,
  // so fetch the assigned list once on mount and keep chips in sync if the parent's
  // data arrives asynchronously.
  useEffect(() => {
    if (alwaysEditable) onEditStart?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (alwaysEditable) setItems(initialItems)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems])

  const handleSearchChange = (e) => {
    const { value } = e.target
    setSearch(value)
    if (onSearchRef.current) debouncedSearch(value)
  }

  const resetSearch = () => {
    debouncedSearch.cancel()
    setSearch("")
  }

  const handleEditClick = (e) => {
    e.stopPropagation()
    setIsEditing(true)
    onEditStart?.()
  }

  const handleDone = (e) => {
    e.stopPropagation()
    setIsEditing(false)
    setIsPickerOpen(false)
    setIsExpanded(false)
    setSelectedIds([])
    resetSearch()
  }

  const dropItem = (id) => setItems((prev) => prev.filter((item) => item.id !== id))

  const handleRemove = (e, id) => {
    e.stopPropagation()
    if (removingId) return
    const removed = items.find((item) => item.id === id)
    if (!removed) return
    const result = onRemove?.(removed)
    // If onRemove returns a promise, keep the chip visible with a spinner until the
    // unassign resolves, then drop it. A non-promise return = optimistic remove.
    if (result && typeof result.then === "function") {
      setRemovingId(id)
      result
        .then(() => dropItem(id))
        .catch(() => {})
        .finally(() => setRemovingId(null))
    } else {
      dropItem(id)
    }
  }

  const handlePickerOpenChange = (open) => {
    if (open) {
      onPickerOpen?.()
    } else {
      setSelectedIds([])
      resetSearch()
    }
    setIsPickerOpen(open)
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const closePicker = () => {
    setIsPickerOpen(false)
    setSelectedIds([])
    resetSearch()
  }

  // In grouped mode the selectable pool is the union of every group's items
  const selectablePool = groups ? groups.flatMap((group) => group.items) : allItems

  const handleAdd = (e) => {
    e.stopPropagation()
    const toAdd = selectablePool.filter((item) => selectedIds.includes(item.id) && !items.some((x) => x.id === item.id))
    if (!toAdd.length) {
      closePicker()
      return
    }

    const result = onAdd?.(toAdd)
    // If onAdd returns a promise, keep the picker open with a spinner until the
    // assign resolves, then commit only the items it confirms were added (the
    // server may partially reject). A non-promise return = optimistic add-all.
    if (result && typeof result.then === "function") {
      setIsAdding(true)
      result
        .then((confirmed) => {
          const committed = Array.isArray(confirmed) ? confirmed : toAdd
          if (committed.length) setItems((prev) => [...prev, ...committed])
        })
        .catch(() => {})
        .finally(() => {
          setIsAdding(false)
          closePicker()
        })
    } else {
      setItems((prev) => [...prev, ...toAdd])
      closePicker()
    }
  }

  const matchesSearch = (item) => item.name.toLowerCase().includes(search.toLowerCase())

  const renderPickerItem = (item) => (
    <div key={item.id} className={styles.pickerItem}>
      <Checkbox
        checked={selectedIds.includes(item.id)}
        onChange={() => toggleSelect(item.id)}
        onClick={(e) => e.stopPropagation()}
      >
        {renderLabel(item)}
      </Checkbox>
    </div>
  )

  const filteredAll = allItems.filter(matchesSearch)
  // Drop groups that have no matches so section headers never render empty
  const visibleGroups = (groups || [])
    .map((group) => ({ ...group, visibleItems: group.items.filter(matchesSearch) }))
    .filter((group) => group.visibleItems.length > 0)

  const renderPickerList = () => {
    if (loading) {
      return (
        <Flex justify="center" align="center" className={styles.pickerLoading}>
          <Spin size="small" />
        </Flex>
      )
    }
    if (groups) {
      if (visibleGroups.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No options" />
      return visibleGroups.map((group) => (
        <div key={group.key} className={styles.pickerGroup}>
          <span className={styles.pickerGroupLabel}>{group.label}</span>
          {group.visibleItems.map(renderPickerItem)}
        </div>
      ))
    }
    if (filteredAll.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No options" />
    return filteredAll.map(renderPickerItem)
  }

  const pickerContent = (
    <div className={styles.pickerDropdown}>
      <Input
        placeholder="Search"
        size="small"
        value={search}
        onChange={handleSearchChange}
        onClick={(e) => e.stopPropagation()}
        className={styles.pickerSearch}
      />
      <div className={styles.pickerList}>{renderPickerList()}</div>
      <button
        type="button"
        className={styles.pickerAddBtn}
        onClick={handleAdd}
        disabled={selectedIds.length === 0 || isAdding}
      >
        {isAdding ? (
          <Spin size="small" />
        ) : (
          <>
            {addLabel}
            {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
          </>
        )}
      </button>
    </div>
  )

  const picker = (
    <Popover content={pickerContent} trigger="click" open={isPickerOpen} onOpenChange={handlePickerOpenChange} placement="bottomLeft">
      <PlusCircleFilled className={styles.addIcon} onClick={(e) => e.stopPropagation()} />
    </Popover>
  )

  // Always-editable variant (booking detail): every chip carries its own ×, and a
  // direct + button opens the picker — no pencil toggle and no "Done" link.
  if (alwaysEditable) {
    if (items.length === 0) {
      return (
        <Flex align="center" gap={8}>
          {emptyLabel && <span className={styles.emptyLabel}>{emptyLabel}</span>}
          {picker}
        </Flex>
      )
    }
    return (
      <Flex wrap gap={4} align="center" onClick={(e) => e.stopPropagation()}>
        {items.map((item) => (
          <Flex key={item.id} align="center" gap={4} className={styles.editChip}>
            {renderLabel(item)}
            {removingId === item.id && removeLoading ? (
              <Spin size="small" />
            ) : (
              <DSIcon name="circledClose" width={16} height={16} fill="var(--color-text-secondary-dark)" className={styles.removeIcon} onClick={(e) => handleRemove(e, item.id)} />
            )}
          </Flex>
        ))}
        {picker}
      </Flex>
    )
  }

  if (isEditing) {
    return (
      <Flex vertical gap={4} onClick={(e) => e.stopPropagation()}>
        <Flex wrap gap={4} align="center">
          {items.map((item) => (
            <Flex key={item.id} align="center" gap={4} className={styles.editChip}>
              {renderLabel(item)}
              {removingId === item.id && removeLoading ? (
                <Spin size="small" />
              ) : (
                <DSIcon name="circledClose" width={16} height={16} fill="var(--color-text-secondary-dark)" className={styles.removeIcon} onClick={(e) => handleRemove(e, item.id)} />
              )}
            </Flex>
          ))}
          {picker}
        </Flex>
        <button type="button" className={styles.doneLink} onClick={handleDone}>
          Done
        </button>
      </Flex>
    )
  }

  if (items.length === 0) {
    const addButton = (
      <Popover
        content={pickerContent}
        trigger="click"
        open={isPickerOpen}
        onOpenChange={handlePickerOpenChange}
        placement="bottomRight"
      >
        <Button type="text" className={styles.addResource} onClick={(e) => e.stopPropagation()}>
          {addLabel}
        </Button>
      </Popover>
    )
    if (!emptyLabel) return addButton
    return (
      <Flex align="center" justify="space-between" gap={8}>
        <span className={styles.emptyLabel}>{emptyLabel}</span>
        {addButton}
      </Flex>
    )
  }

  const hasOverflow = items.length > VISIBLE_COUNT
  const visibleItems = isExpanded ? items : items.slice(0, VISIBLE_COUNT)
  const hiddenCount = items.length - VISIBLE_COUNT
  const showEditIcon = !hasOverflow || isExpanded

  return (
    <Flex align="center" gap={6} wrap>
      {visibleItems.map((item, index) => {
        const isLastVisible = index === visibleItems.length - 1
        // Keep the edit pencil bound to the last chip so it wraps as a unit
        // instead of dropping onto its own line.
        if (isLastVisible && showEditIcon) {
          return (
            <span key={item.id} className={styles.chipWithEdit}>
              <span className={chipClassName}>{renderLabel(item)}</span>
              <EditOutlined className={styles.editIcon} onClick={handleEditClick} />
            </span>
          )
        }
        return (
          <span key={item.id} className={chipClassName}>
            {renderLabel(item)}
          </span>
        )
      })}
      {hasOverflow && !isExpanded && (
        <button
          type="button"
          className={styles.moreLink}
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(true)
          }}
        >
          +{hiddenCount} more
        </button>
      )}
    </Flex>
  )
}

EditableTagCell.propTypes = {
  initialItems: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  allItems: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  // Grouped picker: when provided, the dropdown renders one titled section per group
  // instead of the flat allItems list. Selection/add operate on the union of all groups.
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), name: PropTypes.string }))
    })
  ),
  renderLabel: PropTypes.func.isRequired,
  addLabel: PropTypes.string.isRequired,
  chipClassName: PropTypes.string.isRequired,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  onPickerOpen: PropTypes.func,
  onEditStart: PropTypes.func,
  onSearch: PropTypes.func,
  loading: PropTypes.bool,
  removeLoading: PropTypes.bool,
  emptyLabel: PropTypes.string,
  alwaysEditable: PropTypes.bool
}

EditableTagCell.defaultProps = {
  initialItems: [],
  allItems: [],
  groups: null,
  loading: false,
  removeLoading: false,
  emptyLabel: "",
  alwaysEditable: false
}

export default EditableTagCell
